import { ENTRY_POLICY_ACTIONS } from "../../../util/src/custom-disciplines/entry-policy-contract.js";
import { timerCatalogItems } from "./TimerCatalog";
import dico from "../../../util/src/language/dico.js";
import {
  OPERATOR_NODE_TEMPLATES,
  VARIABLE_NODE_TEMPLATES,
} from "../../../util/src/blueprint/types.js";
import { getValueTypeColor } from "../../../util/src/blueprint/method.js";
import {
  EDITOR_STYLE_ACTION_PROPERTY_LABEL_KEY,
  EDITOR_STYLE_ACTIONS,
} from "../../../util/src/blueprint/editor-style-actions.js";
import {
  CUSTOM_DISCIPLINE_AGE_CATEGORIES_SOURCE,
  CUSTOM_DISCIPLINE_ATHLETE_SOURCE,
  CUSTOM_DISCIPLINE_ATHLETES_SOURCE,
  CUSTOM_DISCIPLINE_CURRENT_GROUP_SOURCE,
  CUSTOM_DISCIPLINE_CURRENT_MEET_SOURCE,
  CUSTOM_DISCIPLINE_CURRENT_VALUE_SOURCE,
  CUSTOM_DISCIPLINE_DISCIPLINES_SOURCE,
  CUSTOM_DISCIPLINE_GROUP_SOURCE,
  CUSTOM_DISCIPLINE_GROUPS_SOURCE,
  CUSTOM_DISCIPLINE_INPUT_VALUES_SOURCE,
  CUSTOM_DISCIPLINE_LEAGUES_SOURCE,
  CUSTOM_DISCIPLINE_ORGANISATIONS_SOURCE,
  CUSTOM_DISCIPLINE_ORGANISATION_SOURCE,
  CUSTOM_DISCIPLINE_LEVELS_SOURCE,
  CUSTOM_DISCIPLINE_RECORDS_SOURCE,
  CUSTOM_DISCIPLINE_REFERENCE_ITEM_SOURCE,
  CUSTOM_DISCIPLINE_REFEREE_LIGHTS_SOURCE,
  CUSTOM_DISCIPLINE_SELECTED_COLUMN_KEY_SOURCE,
  CUSTOM_DISCIPLINE_SELECTED_ROW_ID_SOURCE,
  CUSTOM_DISCIPLINE_WEIGHT_CATEGORIES_SOURCE,
} from "../../../util/src/custom-discipline-behavior/sources.js";
import type {
  BehaviorTemplateCatalogItem,
  BehaviorTemplateCatalogOptions,
} from "./BehaviorTemplateCatalog.js";

const SKIP_LIST_OPERATORS = new Set<string>();

/**
 * Palette variant for the custom-discipline BehaviorCanvas.
 *
 * - `"field"`: field/action graphs (grid fields, toolbar/row actions).
 * - `"cell"`: Display Editor cell-layout node graphs.
 * - `"modal"`: Display Editor modal-layout node graphs.
 * - `"referenceFilter"`: `CustomDisciplineFieldDefinition.referenceFilterBehavior`
 *   graphs, evaluated once per candidate reference-list item to decide whether
 *   it should appear as a selectable FK option.
 */
export type CustomDisciplineBehaviorCatalogMode =
  | "field"
  | "cell"
  | "modal"
  | "referenceFilter"
  | "entryPolicy";

/**
 * Restricted palette for custom-discipline field behaviors.
 *
 * Only `onLoad`, Text Source (`setBinding`), Text style actions, `athlete` /
 * `athletes` variables, and scalar/object/list operators useful in grids.
 * DisplayEditor never calls this (it uses scope `"node"`).
 *
 * @param options - Catalog options; `connectableOnly` filters the picker,
 * `mode` selects the field/action palette or the reference-filter palette.
 * @returns Palette items for the custom-discipline BehaviorCanvas.
 */
export function getCustomDisciplineBehaviorTemplateCatalog({
  connectableOnly = true,
  mode = "field",
}: Pick<BehaviorTemplateCatalogOptions, "connectableOnly"> & {
  mode?: CustomDisciplineBehaviorCatalogMode;
}): BehaviorTemplateCatalogItem[] {
  const isEntryPolicy = mode === "entryPolicy";
  const isReferenceFilter = mode === "referenceFilter" || isEntryPolicy;
  const isCellLayout = mode === "cell";
  const bpDico = dico().behavior;
  const layoutProps = dico().licensedPortal.layoutEditor
    .properties as unknown as Record<string, string>;
  const cdLabels = dico().admin.customDisciplines.behaviorVariables as Record<
    string,
    string
  >;
  const selectedRowIdLabel =
    (cdLabels as Record<string, string>).selectedRowId ?? "Selected row ID";
  const selectedColumnKeyLabel =
    (cdLabels as Record<string, string>).selectedColumnKey ??
    "Selected column key";
  const styleNodeLabels =
    ((bpDico.actions as unknown as Record<string, unknown>).styleNodeLabels as
      | Record<string, string>
      | undefined) ?? {};
  const getStyleActionLabel = (
    key: keyof typeof EDITOR_STYLE_ACTION_PROPERTY_LABEL_KEY,
  ) =>
    styleNodeLabels[key] ??
    layoutProps[EDITOR_STYLE_ACTION_PROPERTY_LABEL_KEY[key]] ??
    key;
  const actionsCategoryLabel = bpDico.nodeList.categories.actions;
  const items: BehaviorTemplateCatalogItem[] = [];
  const pushItem = (item: BehaviorTemplateCatalogItem) => {
    items.push(item);
  };
  const pushObjectVariable = (source: string, label: string) => {
    pushItem({
      id: `variable:object:${source}`,
      label,
      section: "variables",
      categoryLabel: bpDico.nodeList.categories.variables,
      subCategoryLabel: bpDico.nodeList.variableSubCategories.object,
      dotColor: getValueTypeColor("object"),
      template: {
        type: "variable",
        valueType: "object",
        objectSource: source as never,
      },
      isConnectableTarget: false,
    });
  };
  const pushListVariable = (source: string, label: string) => {
    pushItem({
      id: `variable:list:${source}`,
      label,
      section: "variables",
      categoryLabel: bpDico.nodeList.categories.variables,
      subCategoryLabel: bpDico.nodeList.variableSubCategories.lists,
      dotColor: getValueTypeColor("list"),
      template: {
        type: "variable",
        valueType: "list",
        objectSource: source as never,
      },
      isConnectableTarget: false,
    });
  };
  const pushStringVariable = (source: string, label: string) => {
    pushItem({
      id: `variable:string:${source}`,
      label,
      section: "variables",
      categoryLabel: bpDico.nodeList.categories.variables,
      subCategoryLabel: bpDico.nodeList.variableSubCategories.context,
      dotColor: getValueTypeColor("string"),
      template: {
        type: "variable",
        valueType: "string",
        objectSource: source as never,
      },
      isConnectableTarget: false,
    });
  };

  pushItem({
    id: "event:onLoad",
    label: bpDico.events.kinds.onLoad,
    section: "events",
    categoryLabel: bpDico.nodeList.categories.events,
    subCategoryLabel: bpDico.nodeList.eventSubCategories.lifecycle,
    dotColor: getValueTypeColor("event"),
    template: { type: "event", kind: "onLoad" },
    isConnectableTarget: false,
  });

  if (!isReferenceFilter) {
    for (const source of [
      CUSTOM_DISCIPLINE_ATHLETE_SOURCE,
      CUSTOM_DISCIPLINE_ATHLETES_SOURCE,
      CUSTOM_DISCIPLINE_GROUP_SOURCE,
      CUSTOM_DISCIPLINE_CURRENT_GROUP_SOURCE,
      CUSTOM_DISCIPLINE_INPUT_VALUES_SOURCE,
      CUSTOM_DISCIPLINE_REFEREE_LIGHTS_SOURCE,
      CUSTOM_DISCIPLINE_SELECTED_ROW_ID_SOURCE,
      CUSTOM_DISCIPLINE_SELECTED_COLUMN_KEY_SOURCE,
    ]) {
      pushItem({
        id: `event:onChange:${source}`,
        label: cdLabels[source] ?? source,
        section: "events",
        categoryLabel: bpDico.nodeList.categories.events,
        subCategoryLabel: bpDico.nodeList.eventSubCategories.onChange,
        dotColor: getValueTypeColor("event"),
        template: {
          type: "event",
          kind: "onChange",
          objectSource: source as never,
          triggerOnInit: true,
        },
        isConnectableTarget: false,
      });
    }
  }

  if (!isReferenceFilter) {
    pushItem({
      id: "event:onClick",
      label: bpDico.events.kinds.onClick,
      section: "events",
      categoryLabel: bpDico.nodeList.categories.events,
      subCategoryLabel: bpDico.nodeList.eventSubCategories.lifecycle,
      dotColor: getValueTypeColor("event"),
      template: { type: "event", kind: "onClick" },
      isConnectableTarget: false,
    });
  }

  VARIABLE_NODE_TEMPLATES.forEach((template) => {
    pushItem({
      id: `variable:literal:${template.valueType}`,
      label: bpDico.nodeList.inputTypes[template.valueType],
      section: "variables",
      categoryLabel: bpDico.nodeList.categories.variables,
      subCategoryLabel: bpDico.nodeList.variableSubCategories.literal,
      dotColor: getValueTypeColor(template.valueType),
      template,
      isConnectableTarget: false,
    });
  });

  pushObjectVariable(
    CUSTOM_DISCIPLINE_ATHLETE_SOURCE,
    cdLabels.athlete ?? "Athlete",
  );
  pushListVariable(
    CUSTOM_DISCIPLINE_ATHLETES_SOURCE,
    cdLabels.athletes ?? "Athletes",
  );
  if (isCellLayout) {
    pushStringVariable(
      CUSTOM_DISCIPLINE_CURRENT_VALUE_SOURCE,
      cdLabels.currentValue ?? "Current value",
    );
  }
  pushStringVariable(
    CUSTOM_DISCIPLINE_SELECTED_ROW_ID_SOURCE,
    selectedRowIdLabel,
  );
  pushStringVariable(
    CUSTOM_DISCIPLINE_SELECTED_COLUMN_KEY_SOURCE,
    selectedColumnKeyLabel,
  );
  pushObjectVariable(
    CUSTOM_DISCIPLINE_CURRENT_MEET_SOURCE,
    cdLabels.currentMeet ?? "Current meet",
  );
  pushObjectVariable(
    CUSTOM_DISCIPLINE_GROUP_SOURCE,
    cdLabels.group ?? "Context group",
  );
  pushObjectVariable(
    CUSTOM_DISCIPLINE_CURRENT_GROUP_SOURCE,
    cdLabels.currentGroup ?? "Current group",
  );
  pushListVariable(
    CUSTOM_DISCIPLINE_GROUPS_SOURCE,
    cdLabels.groups ?? "Groups",
  );
  pushListVariable(
    CUSTOM_DISCIPLINE_RECORDS_SOURCE,
    cdLabels.records ?? "Records",
  );
  pushListVariable(
    CUSTOM_DISCIPLINE_LEVELS_SOURCE,
    cdLabels.levels ?? "Levels",
  );
  pushListVariable(
    CUSTOM_DISCIPLINE_AGE_CATEGORIES_SOURCE,
    cdLabels.ageCategories ?? "Age categories",
  );
  pushListVariable(
    CUSTOM_DISCIPLINE_WEIGHT_CATEGORIES_SOURCE,
    cdLabels.weightCategories ?? "Weight categories",
  );
  pushListVariable(
    CUSTOM_DISCIPLINE_DISCIPLINES_SOURCE,
    cdLabels.disciplines ?? "Disciplines",
  );
  pushListVariable(
    CUSTOM_DISCIPLINE_LEAGUES_SOURCE,
    cdLabels.leagues ?? "Leagues",
  );
  pushListVariable(
    CUSTOM_DISCIPLINE_ORGANISATIONS_SOURCE,
    cdLabels.organisations ?? "Organisations",
  );
  pushObjectVariable(
    CUSTOM_DISCIPLINE_ORGANISATION_SOURCE,
    cdLabels.organisation ?? "Organisation",
  );
  pushObjectVariable(
    CUSTOM_DISCIPLINE_INPUT_VALUES_SOURCE,
    cdLabels.inputValues ?? "Input values",
  );
  pushObjectVariable(
    CUSTOM_DISCIPLINE_REFEREE_LIGHTS_SOURCE,
    cdLabels.refereeLights ?? "Referee lights",
  );
  if (mode === "referenceFilter") {
    pushObjectVariable(
      CUSTOM_DISCIPLINE_REFERENCE_ITEM_SOURCE,
      cdLabels.referenceItem ?? "Reference item",
    );
  }

  for (const controlType of ["ifElse", "mergeFlows"] as const) {
    pushItem({
      id: `control:${controlType}`,
      label: bpDico.controls[controlType],
      section: "controls",
      categoryLabel: bpDico.nodeList.categories.controls,
      dotColor: getValueTypeColor("control"),
      template: { type: "control", controlType },
      isConnectableTarget: true,
    });
  }

  if (!isReferenceFilter) {
    pushItem({
      id: "action:set:setBinding",
      label: bpDico.actions.setBinding,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "setBinding" },
      isConnectableTarget: true,
    });
  }

  if (isEntryPolicy) {
    for (const key of Object.keys(ENTRY_POLICY_ACTIONS) as Array<
      keyof typeof ENTRY_POLICY_ACTIONS
    >) {
      pushItem({
        id: `action:set:${key}`,
        label: bpDico.actions[key],
        section: "actions",
        categoryLabel: actionsCategoryLabel,
        subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
        dotColor: getValueTypeColor("action"),
        template: { type: "action", kind: "set", key },
        isConnectableTarget: true,
      });
    }
  } else if (isReferenceFilter) {
    pushItem({
      id: "action:set:customReferenceFilter",
      label: bpDico.actions.customReferenceFilter,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customReferenceFilter" },
      isConnectableTarget: true,
    });
  } else {
    pushItem({
      id: "action:set:customActionDisabled",
      label: bpDico.actions.customActionDisabled,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customActionDisabled" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customActionIcon",
      label: bpDico.actions.customActionIcon,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customActionIcon" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customActionVariant",
      label: bpDico.actions.customActionVariant,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customActionVariant" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customActionSize",
      label: bpDico.actions.customActionSize,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customActionSize" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customActionColor",
      label: bpDico.actions.customActionColor,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customActionColor" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customActionTooltip",
      label: bpDico.actions.customActionTooltip,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customActionTooltip" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customActionLabel",
      label: bpDico.actions.customActionLabel,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customActionLabel" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customInputOptions",
      label: bpDico.actions.customInputOptions,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customInputOptions" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customListSetField",
      label: bpDico.actions.customListSetField,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customListSetField" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customListSetRows",
      label: bpDico.actions.customListSetRows,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customListSetRows" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customListSetViewRows",
      label: bpDico.actions.customListSetViewRows,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customListSetViewRows" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customGroupSetField",
      label: bpDico.actions.customGroupSetField,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customGroupSetField" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customOpenGroup",
      label: bpDico.actions.customOpenGroup,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customOpenGroup" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customSetCurrentGroup",
      label: bpDico.actions.customSetCurrentGroup,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customSetCurrentGroup" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customSetCurrentAthlete",
      label: bpDico.actions.customSetCurrentAthlete,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: {
        type: "action",
        kind: "set",
        key: "customSetCurrentAthlete",
      },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customOpenModal",
      label: bpDico.actions.customOpenModal,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customOpenModal" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customCloseModal",
      label: bpDico.actions.customCloseModal,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customCloseModal" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customPrintModal",
      label: bpDico.actions.customPrintModal,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customPrintModal" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customSubmitForm",
      label: bpDico.actions.customSubmitForm,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customSubmitForm" },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customIncrementAthleteCount",
      label: bpDico.actions.customIncrementAthleteCount,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: {
        type: "action",
        kind: "set",
        key: "customIncrementAthleteCount",
      },
      isConnectableTarget: true,
    });
    pushItem({
      id: "action:set:customRunAction",
      label: bpDico.actions.customRunAction,
      section: "actions",
      categoryLabel: actionsCategoryLabel,
      subCategoryLabel: bpDico.nodeList.actionSubCategories.content,
      dotColor: getValueTypeColor("action"),
      template: { type: "action", kind: "set", key: "customRunAction" },
      isConnectableTarget: true,
    });
  }

  if (!isReferenceFilter) {
    EDITOR_STYLE_ACTIONS.forEach((definition) => {
      pushItem({
        id: `action:set:${definition.key}`,
        label: getStyleActionLabel(definition.key),
        section: "actions",
        categoryLabel: actionsCategoryLabel,
        subCategoryLabel: bpDico.nodeList.actionSubCategories.style,
        dotColor: getValueTypeColor("action"),
        template: { type: "action", kind: "set", key: definition.key },
        isConnectableTarget: true,
      });
    });
  }

  (
    Object.keys(OPERATOR_NODE_TEMPLATES) as Array<
      keyof typeof OPERATOR_NODE_TEMPLATES
    >
  ).forEach((inputType) => {
    OPERATOR_NODE_TEMPLATES[inputType].forEach((template) => {
      if (
        inputType === "list" &&
        SKIP_LIST_OPERATORS.has(String(template.operator))
      ) {
        return;
      }
      pushItem({
        id: `operator:${inputType}:${template.operator}`,
        label: bpDico.operators[template.operator] ?? String(template.operator),
        section: "operators",
        categoryLabel: bpDico.nodeList.categories.operators,
        subCategoryLabel: bpDico.nodeList.inputTypes[inputType],
        dotColor: getValueTypeColor(inputType),
        template,
        isConnectableTarget: true,
      });
    });
  });

  items.push(...timerCatalogItems(!isReferenceFilter));
  return connectableOnly
    ? items.filter((item) => item.isConnectableTarget)
    : items;
}
